import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, type LeadQuery } from '@/api/client';
import type { LeadPatch } from '@/api/types';
import { useDateRange } from './useOverview';

/**
 * State behind the Leads tab: the shared date range, the lead-specific filters, and the two
 * queries that read them.
 *
 * Filters live in the URL for the same reason the range does — a filtered view is the thing people
 * paste to each other, and it has to survive a refresh. `page` resets whenever a filter changes,
 * because page 4 of an unfiltered list is usually past the end of a filtered one, and an empty
 * table with no explanation reads as "no leads".
 */
export function useLeads(siteKey: string) {
  const range = useDateRange();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  const source = params.get('source') ?? '';
  const status = params.get('status') ?? '';
  const country = params.get('country') ?? '';
  const search = params.get('q') ?? '';
  const page = Number(params.get('page')) || 1;
  const perPage = Number(params.get('per_page')) || 25;

  const setFilter = (key: 'source' | 'status' | 'country' | 'q', value: string) => {
    const next = new URLSearchParams(params);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.delete('page');
    setParams(next, { replace: true });
  };

  const setPage = (value: number) => {
    const next = new URLSearchParams(params);
    next.set('page', String(value));
    setParams(next, { replace: true });
  };

  const setPerPage = (value: number) => {
    const next = new URLSearchParams(params);
    next.set('per_page', String(value));
    next.delete('page');
    setParams(next, { replace: true });
  };

  const clearFilters = () => {
    const next = new URLSearchParams(params);
    for (const key of ['source', 'status', 'country', 'q', 'page']) next.delete(key);
    setParams(next, { replace: true });
  };

  // Memoised so it is a stable query-key member — a fresh object each render would refetch on
  // every keystroke elsewhere on the page.
  const filter = useMemo<LeadQuery>(
    () => ({
      from: range.from,
      to: range.to,
      source: source || undefined,
      status: status || undefined,
      country: country || undefined,
      q: search || undefined,
    }),
    [range.from, range.to, source, status, country, search],
  );

  const summary = useQuery({
    queryKey: ['leads-summary', siteKey, filter],
    queryFn: () => api.leads.summary(siteKey, filter),
  });

  const list = useQuery({
    queryKey: ['leads-list', siteKey, filter, page, perPage],
    queryFn: () => api.leads.list(siteKey, filter, page, perPage),
    // Keeps the previous page on screen while the next one loads, so the table does not collapse
    // to an empty state and back on every page change.
    placeholderData: (previous) => previous,
  });

  /**
   * Moving a stage changes the KPIs above the table, so BOTH queries are invalidated — patching
   * only the row would leave the pipeline total contradicting the row the user just edited.
   */
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: LeadPatch }) => api.leads.patch(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leads-summary', siteKey] });
      void queryClient.invalidateQueries({ queryKey: ['leads-list', siteKey] });
    },
  });

  const sync = useMutation({
    mutationFn: () => api.leads.sync(siteKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leads-summary', siteKey] });
      void queryClient.invalidateQueries({ queryKey: ['leads-list', siteKey] });
    },
  });

  return {
    ...range,
    filter,
    source, status, country, search,
    page, perPage,
    setFilter, setPage, setPerPage, clearFilters,
    hasFilters: Boolean(source || status || country || search),
    summary,
    list,
    patch,
    sync,
  };
}

/** The Leads section's sub-views. In the URL so a view survives a refresh and can be pasted. */
export const LEAD_VIEWS = ['pipeline', 'network', 'intelligence'] as const;
export type LeadView = (typeof LEAD_VIEWS)[number];

export function useLeadView(): [LeadView, (view: LeadView) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('view');
  const view = LEAD_VIEWS.includes(raw as LeadView) ? (raw as LeadView) : 'pipeline';

  const setView = (next: LeadView) => {
    const updated = new URLSearchParams(params);
    if (next === 'pipeline') {
      updated.delete('view');
    } else {
      updated.set('view', next);
    }
    // The table's page number belongs to the table; carrying it onto the graph and back would
    // silently restore a page that the filter may since have emptied.
    updated.delete('page');
    setParams(updated, { replace: true });
  };

  return [view, setView];
}

/**
 * The similarity graph for the current filter.
 *
 * `enabled` is load-bearing: this is the one lead read that costs real CPU on the server (an
 * O(V·E) betweenness pass), so it must not run while the user is looking at the pipeline view.
 */
export function useLeadNetwork(siteKey: string, filter: LeadQuery, enabled: boolean, maxNodes = 300) {
  return useQuery({
    queryKey: ['leads-network', siteKey, filter, maxNodes],
    queryFn: () => api.leads.network(siteKey, filter, maxNodes),
    enabled,
    // The graph is a pure function of rows that only change on a sync, and recomputing it is the
    // expensive part — so a longer stale window here is correctness-neutral and saves the CPU.
    staleTime: 60_000,
  });
}

// The AI insights query lives inside `components/LeadInsights.tsx`, which owns that whole surface
// (read + run + render). A second hook here would be a second query key over one endpoint — the
// exact duplication that lets two views of the same data disagree.

/** One lead in full. Its own query key so the list cache is untouched by opening a profile. */
export function useLeadProfile(id: string | undefined) {
  return useQuery({
    queryKey: ['lead-profile', id],
    queryFn: () => api.leads.profile(id!),
    enabled: Boolean(id),
  });
}
